import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { PasswordUnlockDialog } from './PasswordUnlockDialog';
import { setUnlockListener } from '../lib/unlock';
import { askOtherTabs, onOtherTabUnlocked } from '../lib/keyShare';
import { setPrivateKey } from '../lib/crypto';

/**
 * The single password prompt behind lib/unlock.js. Mounted once in App.js
 * for signed-in pages: when a response carries sealed details this tab can't
 * open, or an action needs the key (HTTP 423), lib/api.js asks here.
 */
export const UnlockGate = () => {
  const { user } = useAuth();
  const [ask, setAsk] = useState(null);

  useEffect(() => {
    if (!user) return undefined;
    setUnlockListener((next) => setAsk(next));
    return () => setUnlockListener(null);
  }, [user]);

  // Unlocked in another tab while this one waits: take the key from there.
  useEffect(() => {
    if (!ask) return undefined;
    return onOtherTabUnlocked(async () => {
      const key = await askOtherTabs();
      if (key) {
        setPrivateKey(key);
        ask.resolve(key);
        setAsk(null);
      }
    });
  }, [ask]);

  if (!ask) return null;
  return (
    <PasswordUnlockDialog
      open
      reason={ask.reason}
      onOpenChange={(open) => {
        if (!open) {
          ask.reject(new Error('Not unlocked'));
          setAsk(null);
        }
      }}
      onUnlocked={(privateKey) => {
        ask.resolve(privateKey); // settled, so the close that follows can't reject it
        setAsk(null);
      }}
    />
  );
};

export default UnlockGate;
